import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import i18n from '../../i18n';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Radio,
  RadioGroup,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  AutoAwesomeRounded,
  PictureAsPdfRounded,
  RestartAltRounded,
  SendRounded,
  SaveRounded,
} from '@mui/icons-material';
import { pdf } from '@react-pdf/renderer';
import { openGeneratedPdf } from '../../lib/open-generated-pdf';
import { doc, getDoc, collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import type { AcademicLevel, CapCode, CapProposal, CapProposalSectionKey } from '@dosfilos/domain';
import { db } from '../../lib/firebase';
import { useTenantStore } from '../../stores/tenant.store';
import { capApi, type CapProposalVersionSummary } from '../../lib/api/cap.api';
import { useProgramCapTarget } from '../../hooks/use-program-cap-target';
import { CapPageHeader, CAP_CONTENT_MAX_WIDTH } from './components/CapPageHeader';
import { CapProposalPdfDocument, type CapProposalPdfData } from './CapProposalPdfDocument';
import {
  CapNetworkReviewPanel,
  REVIEW_STATUS_COLOR,
  useCapNetworkReview,
} from './components/CapNetworkReviewPanel';
import { proposalReviewActions } from '../../lib/cap-review-actions';
import { errorParams, errorReason, errorText } from '../../lib/error-text';
import { formatDate, inLocale } from '../../lib/i18n-format';

// ─── CAP proposal generator (Phase E — CAP, PR7) — wireframe screen 4 ───────
// Assembles the A–K packet, lets the registrar fill manual sections + attest,
// and moves it through draft→ready→submitted. The PDF is client-side and
// always English (§III.A), independent of the UI language.

const MANUAL_KEYS: CapProposalSectionKey[] = ['A', 'C', 'D', 'F', 'K'];

export function CapProposalPage(): JSX.Element {
  const { t } = useTranslation('cap');
  const navigate = useNavigate();
  const { programId } = useParams<{ programId: string }>();
  const { currentTenantId } = useTenantStore();

  const [capCode, setCapCode] = useState<CapCode>('BSC');
  const [level, setLevel] = useState<AcademicLevel>('undergraduate');

  // Default CAP + level to the program's target (still switchable).
  const capTarget = useProgramCapTarget(programId);
  useEffect(() => {
    if (!capTarget) return;
    setCapCode(capTarget.capCode);
    setLevel(capTarget.level);
  }, [capTarget]);
  const [proposal, setProposal] = useState<CapProposal | null>(null);
  const [content, setContent] = useState<Record<string, string>>({});
  const [cvsAttested, setCvsAttested] = useState(false);
  const [gapsAttested, setGapsAttested] = useState(false);
  const [versions, setVersions] = useState<CapProposalVersionSummary[]>([]);
  // Non-null while previewing an archived (superseded) version read-only.
  const [viewingArchivedId, setViewingArchivedId] = useState<string | null>(null);
  /** Diálogo de presentación a la red: `null` cerrado, o el tipo elegido. */
  const [sendKind, setSendKind] = useState<'advisory' | 'formal' | null>(null);
  const [loading, setLoading] = useState(true);
  /** QUÉ acción está corriendo, no solo que hay una. Con un booleano compartido,
   *  guardar encendía el spinner de todos los botones y parecía que se habían
   *  disparado todos. */
  const [busyAction, setBusyAction] = useState<
    null | 'assemble' | 'save' | 'reassemble' | 'transition' | 'reopen' | 'send' | 'pdf' | 'version'
  >(null);
  const busy = busyAction !== null;
  /** La auditoría contra la que se va a presentar. Se muestra EN el diálogo: el
   *  centro estaba firmando "someto con brechas conocidas" sin tener las brechas
   *  a la vista. */
  const [audit, setAudit] = useState<{
    overallStatus: 'compliant' | 'with_gaps';
    totalMinTcu: number;
    totalActualTcu: number;
    gaps: string[];
    plosPending: number;
    plosTotal: number;
    nonAcademicPending: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const review = useCapNetworkReview(currentTenantId, programId, capCode, level);

  const hydrate = useCallback((p: CapProposal | null) => {
    setProposal(p);
    setContent(
      Object.fromEntries(
        (p?.sections ?? []).filter((s) => s.content).map((s) => [s.key, s.content!]),
      ),
    );
    setCvsAttested(!!p?.attestations.find((a) => a.key === 'faculty_cvs_attached')?.met);
    setGapsAttested(!!p?.attestations.find((a) => a.key === 'submitting_with_known_gaps')?.met);
  }, []);

  // Staleness guard: capCode/level flip (BSC default → program's capTarget) fire
  // two loads; only the latest response may apply so the older doesn't overwrite it.
  const loadReq = useRef(0);
  const load = useCallback(async () => {
    if (!currentTenantId || !programId) return;
    const req = ++loadReq.current;
    setLoading(true);
    setError(null);
    try {
      const res = await capApi.getProposal({
        tenantId: currentTenantId,
        programId,
        capCode,
        level,
      });
      if (loadReq.current === req) {
        hydrate(res.proposal);
        setVersions(res.versions);
        setViewingArchivedId(null);
        setAudit(await loadAuditSummary(currentTenantId, programId, capCode, level));
      }
    } catch (e) {
      if (loadReq.current === req) setError(errorText(e));
    } finally {
      if (loadReq.current === req) setLoading(false);
    }
  }, [currentTenantId, programId, capCode, level, hydrate]);

  useEffect(() => {
    void load();
  }, [load]);

  const readOnly = proposal?.status === 'submitted';

  const attestationsPayload = (): {
    key: 'faculty_cvs_attached' | 'submitting_with_known_gaps';
    met: boolean;
  }[] => [
    { key: 'faculty_cvs_attached', met: cvsAttested },
    { key: 'submitting_with_known_gaps', met: gapsAttested },
  ];

  const assemble = async () => {
    if (!currentTenantId || !programId) return;
    setBusyAction('assemble');
    setError(null);
    try {
      hydrate(
        await capApi.assembleProposal({ tenantId: currentTenantId, programId, capCode, level }),
      );
      setMsg(t('proposal.assembled'));
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusyAction(null);
    }
  };

  const save = async () => {
    if (!currentTenantId || !proposal || !programId) return;
    setBusyAction('save');
    setError(null);
    try {
      await capApi.saveProposal({
        tenantId: currentTenantId,
        proposalId: proposal.id,
        content,
        attestations: attestationsPayload(),
      });
      // Guardar sólo persistía el texto manual, así que las secciones AUTOMÁTICAS
      // seguían mostrando el ensamblado anterior: la pantalla marcaba huecos que
      // los datos ya no tenían (una facultad sin docentes después de crear las
      // secciones) y el coordinador buscaba un problema inexistente. Re-ensamblar
      // acá es lo mismo que hace Presentar, en el mismo orden y preservando el
      // texto manual — sólo que ahora se ve antes de firmar nada.
      hydrate(
        await capApi.assembleProposal({
          tenantId: currentTenantId,
          programId,
          capCode,
          level,
        }),
      );
      setMsg(t('proposal.saved'));
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusyAction(null);
    }
  };

  /** Qué secciones bloquean el paso a "lista". El servidor responde
   *  `cap.sections_incomplete` sin decir cuáles, y buscarlas a ojo en una lista
   *  de trece es trabajo que la pantalla ya tiene resuelto. */
  /** Los nombres de las secciones que bloquean, TRADUCIDOS acá pero NOMBRADOS
   *  por el servidor: viajan en `params.sections` del error, separados por coma.
   *
   *  Antes se calculaban desde `proposal` en memoria, que al presentar ya es
   *  vieja —el servidor re-ensambla antes de validar— y el mensaje llegó a
   *  acusar una sección que ya estaba resuelta. Si un servidor viejo no manda
   *  la lista, se cae a la copia local: peor información, pero no ninguna. */
  const incompleteSections = (e: unknown): string => {
    const fromServer = (errorParams(e)?.['sections'] as string | undefined) ?? '';
    const keys = fromServer
      ? fromServer.split(',').filter(Boolean)
      : (proposal?.sections ?? []).filter((s) => s.hardGap || !s.complete).map((s) => s.key);
    return keys.map((k) => t(`proposal.sections.${k}`)).join(', ');
  };

  const reopen = async () => {
    if (!currentTenantId || !proposal) return;
    setBusyAction('reopen');
    setError(null);
    try {
      await capApi.reopenProposal({ tenantId: currentTenantId, proposalId: proposal.id });
      await load(); // active is now the new draft; refreshes the version history
      setMsg(t('proposal.reopen.done'));
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusyAction(null);
    }
  };

  /** Presenta la propuesta a la red. Lo que se publica lo arma el servidor: acá
   *  solo viaja qué propuesta y si es consulta o solicitud. */
  const sendToNetwork = async (submissionKind: 'advisory' | 'formal') => {
    if (!currentTenantId || !proposal) return;
    setBusyAction('send');
    setError(null);
    try {
      const summary = await capApi.presentProposal({
        tenantId: currentTenantId,
        proposalId: proposal.id,
        submissionKind,
        attestations: attestationsPayload(),
      });
      setSendKind(null);
      setMsg(t('proposal.network.done', { round: summary.round }));
      await Promise.all([review.reload(), load()]);
    } catch (e) {
      // El servidor valida al presentar: si falta una sección, decir CUÁL.
      const missing = errorReason(e) === 'cap.sections_incomplete' ? incompleteSections(e) : '';
      setError(
        missing
          ? `${errorText(e)} ${t('proposal.incompleteSections', { sections: missing })}`
          : errorText(e),
      );
    } finally {
      setBusyAction(null);
    }
  };

  // Preview an archived (superseded) version read-only. capProposals are
  // staff-readable, so we fetch the frozen doc directly (getProposal only ever
  // returns the active version).
  const viewVersion = async (id: string) => {
    if (!currentTenantId) return;
    setBusyAction('version');
    setError(null);
    try {
      const snap = await getDoc(doc(db, `tenants/${currentTenantId}/capProposals/${id}`));
      if (!snap.exists()) throw new Error(t('proposal.versions.notFound'));
      hydrate(snap.data() as CapProposal);
      setViewingArchivedId(id);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusyAction(null);
    }
  };

  const backToActive = () => void load();

  const previewPdf = async () => {
    if (!proposal) return;
    setBusyAction('pdf');
    setError(null);
    try {
      // The tab opens inside the click; the proposal is generated into it.
      await openGeneratedPdf(async () => {
        // TMAI requires English submission — resolve labels/headings with a
        // fixed 'en' translator, regardless of the UI language.
        const en = i18n.getFixedT('en', 'cap');
        // Tenant name + TMAI site abbreviation (Appendix A field e) from the
        // tenant doc (public-read); best-effort. Header shows the NAME, not the id.
        let tenantName = currentTenantId ?? '';
        let siteAbbreviation = '';
        if (currentTenantId) {
          try {
            const tSnap = await getDoc(doc(db, `tenants/${currentTenantId}`));
            const tData = tSnap.data() ?? {};
            const cfg = (tData['config'] ?? {}) as { tmaiSiteAbbreviation?: string };
            siteAbbreviation = cfg.tmaiSiteAbbreviation ?? '';
            tenantName = (tData['name'] as string | undefined) ?? tenantName;
          } catch {
            /* best-effort */
          }
        }
        // El NOMBRE del programa, no su id. El PDF imprimía el uuid en el campo
        // "PROGRAM" del encabezado, que es lo primero que lee TMAI.
        let programName = '';
        if (currentTenantId && programId) {
          try {
            const pSnap = await getDoc(doc(db, `tenants/${currentTenantId}/programs/${programId}`));
            const pData = pSnap.data() ?? {};
            const code = pData['code'] as string | undefined;
            const name = (pData['name'] as string | undefined) ?? '';
            programName = code && name ? `${code} — ${name}` : name;
          } catch {
            /* best-effort */
          }
        }
        // Itemized compliance gaps from the referenced audit — the section-level
        // view doesn't surface them; the packet reviewer needs the specific
        // shortfalls, not just the "known gaps" banner. Las áreas no son la única
        // brecha que la auditoría registró: un PLO sin evaluar y un requisito no
        // académico sin atestar también lo son, y omitirlos volvía la declaración
        // "someto con brechas conocidas" más chica de lo que era.
        let complianceGaps: string[] = [];
        if (currentTenantId) {
          try {
            let report: Record<string, unknown> | undefined;
            if (proposal.lastReportId) {
              report = (
                await getDoc(
                  doc(
                    db,
                    `tenants/${currentTenantId}/capComplianceReports/${proposal.lastReportId}`,
                  ),
                )
              ).data();
            }
            if (!report && programId) {
              const snap = await getDocs(
                query(
                  collection(db, `tenants/${currentTenantId}/capComplianceReports`),
                  where('programId', '==', programId),
                  where('capCode', '==', proposal.capCode),
                  orderBy('generatedAt', 'desc'),
                  limit(1),
                ),
              );
              report = snap.docs[0]?.data();
            }
            const areas = (report?.['subjectAreas'] ?? []) as Array<{
              code: string;
              gapTcu: number;
            }>;
            const plos = (report?.['ploCoverage'] ?? []) as Array<{ evaluated: boolean }>;
            const nonAcademic = (report?.['nonAcademic'] ?? []) as Array<{
              key: string;
              met: boolean;
            }>;
            const plosPending = plos.filter((p) => !p.evaluated).length;
            complianceGaps = [
              ...areas
                .filter((a) => a.gapTcu > 0)
                .map((a) => `${en(`subjectAreas.${a.code}`)} — ${a.gapTcu} TCU`),
              ...(plosPending > 0
                ? [en('proposal.pdf.gapPlosNotEvaluated', { n: plosPending, total: plos.length })]
                : []),
              ...nonAcademic
                .filter((n) => !n.met)
                .map((n) =>
                  en('proposal.pdf.gapNonAcademic', {
                    name: en(`compliance.nonAcademicKeys.${n.key}`),
                  }),
                ),
            ];
          } catch {
            /* best-effort */
          }
        }
        const data: CapProposalPdfData = {
          brand: {
            tenantName,
            primaryColor: '#334155',
            ...(siteAbbreviation ? { tmaiSiteAbbreviation: siteAbbreviation } : {}),
          },
          programName: programName || (programId ?? ''),
          award: proposal.award.en,
          capCode: proposal.capCode,
          level: en(`levels.${proposal.level}`),
          generatedOn: inLocale('en-US').date(new Date()),
          status: proposal.status,
          ...(proposal.status !== 'draft' &&
          proposal.attestations.find((a) => a.key === 'submitting_with_known_gaps')?.met
            ? { knownGapsDisclosure: en('proposal.pdf.knownGapsDisclosure') }
            : {}),
          ...(complianceGaps.length > 0 ? { complianceGaps } : {}),
          sections: proposal.sections.map((s) => {
            // Manual narrative wins; auto sections fall back to their English
            // data summary so the PDF shows real content, not a placeholder.
            const body = s.content ?? s.autoSummary;
            return {
              key: s.key,
              heading: en(`proposal.sections.${s.key}`),
              source: s.source,
              complete: s.complete,
              hardGap: !!s.hardGap,
              ...(body ? { body } : {}),
              // Section D language-rule advisory (English for the packet). The
              // warning is derived from CURRENT content, never persisted.
              ...(s.languageRuleApplies ? { note: en('proposal.pdf.languageRuleNote') } : {}),
              ...(s.languageRuleApplies && !s.content?.trim()
                ? { warning: en('proposal.pdf.languageRuleWarning') }
                : {}),
            };
          }),
          labels: {
            title: en('proposal.pdf.title'),
            watermarkDraft: en('proposal.pdf.watermarkDraft'),
            program: en('proposal.pdf.program'),
            award: en('proposal.pdf.award'),
            cap: en('proposal.pdf.cap'),
            level: en('proposal.pdf.level'),
            generatedOn: en('proposal.pdf.generatedOn'),
            sourceAuto: en('proposal.pdf.sourceAuto'),
            sourceManual: en('proposal.pdf.sourceManual'),
            hardGap: en('proposal.pdf.hardGap'),
            incomplete: en('proposal.pdf.incomplete'),
            footerDisclaimer: en('proposal.pdf.footerDisclaimer'),
            complianceGapsTitle: en('proposal.pdf.complianceGapsTitle'),
          },
        };
        return pdf(<CapProposalPdfDocument data={data} />).toBlob();
      }, 'cap-proposal');
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusyAction(null);
    }
  };

  // Qué ofrecer después de someter, según lo que respondió TMAI. El servidor
  // rechaza una segunda ronda con otra abierta; ofrecer el botón igual era invitar
  // a un error. Tras una solicitud devuelta o rechazada, lo que corresponde es una
  // versión nueva, no volver a mandar la misma evidencia.
  const latestRound = proposal
    ? review.rounds.filter((r) => r.proposalId === proposal.id).sort((a, b) => b.round - a.round)[0]
    : undefined;
  const { canPresent, asksNewVersion } = proposal
    ? proposalReviewActions({
        rounds: review.rounds,
        certification: review.certification,
        proposalId: proposal.id,
      })
    : { canPresent: false, asksNewVersion: false };

  const completeCount = proposal?.sections.filter((s) => s.complete).length ?? 0;
  const total = proposal?.sections.length ?? 0;
  const pct = total > 0 ? Math.round((completeCount / total) * 100) : 0;

  return (
    <Box>
      <CapPageHeader
        backLabel={t('proposal.back')}
        onBack={() => navigate(-1)}
        title={t('proposal.title')}
        subtitle={t('proposal.subtitle')}
      />
      <Box sx={{ px: 3, py: 3, pb: 6, maxWidth: CAP_CONTENT_MAX_WIDTH, mx: 'auto' }}>
        <Stack direction="row" spacing={2} mb={2} flexWrap="wrap" alignItems="center">
          <TextField
            select
            size="small"
            label={t('proposal.cap')}
            value={capCode}
            onChange={(e) => setCapCode(e.target.value as CapCode)}
            sx={{ minWidth: 110 }}
            disabled={!!proposal}
          >
            <MenuItem value="BSC">{t('plos.caps.BSC')}</MenuItem>
            <MenuItem value="DEP">{t('plos.caps.DEP')}</MenuItem>
          </TextField>
          <TextField
            select
            size="small"
            label={t('proposal.level')}
            value={level}
            onChange={(e) => setLevel(e.target.value as AcademicLevel)}
            sx={{ minWidth: 150 }}
            disabled={!!proposal}
          >
            <MenuItem value="undergraduate">{t('levels.undergraduate')}</MenuItem>
            <MenuItem value="graduate">{t('levels.graduate')}</MenuItem>
          </TextField>
          {proposal && (
            <Chip
              color={
                proposal.status === 'submitted'
                  ? 'success'
                  : proposal.status === 'ready'
                    ? 'primary'
                    : 'default'
              }
              label={t(`proposal.status.${proposal.status}`)}
            />
          )}
          {/* El estado de la propuesta describe el DOCUMENTO ("sometida" =
              congelado); el de la ronda, qué dijo TMAI. Al lado de una solicitud
              devuelta, "Sometida" sola se lee como si nadie hubiera respondido. */}
          {latestRound && (
            <Chip
              color={REVIEW_STATUS_COLOR[latestRound.status]}
              variant="outlined"
              label={t(`proposal.review.status.${latestRound.status}`)}
            />
          )}
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ height: 4, mb: 1.5 }}>
          {loading && proposal && <LinearProgress sx={{ borderRadius: 4 }} />}
        </Box>

        {loading && !proposal ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : !proposal ? (
          <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
            <Typography mb={2}>{t('proposal.empty')}</Typography>
            <Button
              variant="contained"
              startIcon={
                busyAction === 'assemble' ? <CircularProgress size={16} /> : <AutoAwesomeRounded />
              }
              disabled={busy}
              onClick={() => void assemble()}
            >
              {t('proposal.assembleCta')}
            </Button>
          </Paper>
        ) : (
          <>
            {viewingArchivedId && (
              <Alert
                severity="info"
                sx={{ mb: 2 }}
                action={
                  <Button color="inherit" size="small" onClick={backToActive} disabled={busy}>
                    {t('proposal.versions.backToActive')}
                  </Button>
                }
              >
                {t('proposal.versions.viewingArchived', { version: proposal.version })}
              </Alert>
            )}
            <CapNetworkReviewPanel review={review} currentProposalId={proposal.id} />
            <Box mb={2}>
              <Typography variant="caption" color="text.secondary">
                {t('proposal.autopopulated', { pct })}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={pct}
                sx={{ mt: 0.75, height: 8, borderRadius: 4 }}
              />
            </Box>

            <Stack spacing={2} mb={2}>
              {proposal.sections.map((s) => {
                const isManual = MANUAL_KEYS.includes(s.key as CapProposalSectionKey);
                const accent = s.hardGap
                  ? 'error.main'
                  : s.complete
                    ? 'success.main'
                    : 'warning.main';
                return (
                  <Paper
                    key={s.key}
                    variant="outlined"
                    sx={{
                      p: 2.5,
                      borderRadius: 3,
                      borderLeft: '4px solid',
                      borderLeftColor: accent,
                      transition: 'box-shadow .15s',
                      '&:hover': { boxShadow: 2 },
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      mb={isManual ? 1.5 : 0}
                      flexWrap="wrap"
                    >
                      <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
                        {t(`proposal.sections.${s.key}`)}
                      </Typography>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={s.source === 'auto' ? 'success' : 'info'}
                        label={s.source === 'auto' ? t('proposal.auto') : t('proposal.manual')}
                      />
                      {s.hardGap ? (
                        <Chip size="small" color="error" label={t('proposal.hardGap')} />
                      ) : s.complete ? (
                        <Chip size="small" color="success" label={t('proposal.complete')} />
                      ) : (
                        <Chip size="small" color="warning" label={t('proposal.pending')} />
                      )}
                    </Stack>
                    {s.languageRuleApplies && (
                      <Alert severity="info" sx={{ mb: 1 }}>
                        {t('proposal.languageRuleNote')}
                      </Alert>
                    )}
                    {s.languageRuleApplies && !(content[s.key] ?? '').trim() && (
                      <Alert severity="warning" sx={{ mb: 1 }}>
                        {t('proposal.languageRuleWarning')}
                      </Alert>
                    )}
                    {isManual && (
                      <TextField
                        value={content[s.key] ?? ''}
                        onChange={(e) => setContent((c) => ({ ...c, [s.key]: e.target.value }))}
                        placeholder={t(`proposal.placeholders.${s.key}`)}
                        helperText={
                          s.key === 'A' && !s.complete && (content.A ?? '').trim()
                            ? t('proposal.purposeSeedHelp')
                            : undefined
                        }
                        multiline
                        minRows={2}
                        fullWidth
                        disabled={readOnly}
                      />
                    )}
                  </Paper>
                );
              })}
            </Stack>

            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button
                variant="outlined"
                startIcon={<PictureAsPdfRounded />}
                disabled={busy}
                onClick={() => void previewPdf()}
              >
                {t('proposal.previewPdf')}
              </Button>
              {!readOnly && (
                <Button
                  variant="outlined"
                  startIcon={
                    busyAction === 'save' ? <CircularProgress size={16} /> : <SaveRounded />
                  }
                  disabled={busy}
                  onClick={() => void save()}
                >
                  {t('proposal.save')}
                </Button>
              )}
              {!proposal.supersededById && !viewingArchivedId && (
                <>
                  {/* DOS ACCIONES, no cinco. Ensamblar contra la última auditoría,
                      validar, congelar y cruzar a la red pasaron al servidor: eran
                      derivación, no decisiones del centro. Lo que sí decide —pedir
                      comentarios o la certificación, y firmar las atestaciones—
                      vive en el diálogo. */}
                  {canPresent && (
                    <Button
                      variant="contained"
                      startIcon={
                        busyAction === 'send' ? <CircularProgress size={16} /> : <SendRounded />
                      }
                      disabled={busy}
                      onClick={() => {
                        setError(null); // un error viejo no describe este intento
                        setSendKind('formal');
                      }}
                    >
                      {t('proposal.network.cta')}
                    </Button>
                  )}
                  {/* Reabrir sólo tiene sentido sobre algo congelado; tras una
                      solicitud devuelta es LA respuesta, así que pasa a primaria. */}
                  {proposal.status === 'submitted' && (
                    <Button
                      variant={asksNewVersion ? 'contained' : 'outlined'}
                      startIcon={
                        busyAction === 'reopen' ? (
                          <CircularProgress size={16} />
                        ) : (
                          <RestartAltRounded />
                        )
                      }
                      disabled={busy}
                      onClick={() => void reopen()}
                    >
                      {t('proposal.reopen.cta')}
                    </Button>
                  )}
                </>
              )}
            </Stack>

            {versions.length > 1 && (
              <Paper variant="outlined" sx={{ p: 2.5, mt: 2, borderRadius: 3 }}>
                <Typography variant="subtitle1" fontWeight={700} mb={1.5}>
                  {t('proposal.versions.title')}
                </Typography>
                <Stack spacing={1}>
                  {versions.map((v) => {
                    const isViewing = viewingArchivedId === v.id;
                    const isCurrentView = v.active ? !viewingArchivedId : isViewing;
                    return (
                      <Stack
                        key={v.id}
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        flexWrap="wrap"
                        sx={{
                          p: 1,
                          borderRadius: 2,
                          bgcolor: isCurrentView ? 'action.selected' : 'transparent',
                        }}
                      >
                        <Typography fontWeight={600}>
                          {t('proposal.versions.vLabel', { n: v.version })}
                        </Typography>
                        <Chip
                          size="small"
                          variant="outlined"
                          color={
                            v.status === 'submitted'
                              ? 'success'
                              : v.status === 'ready'
                                ? 'primary'
                                : 'default'
                          }
                          label={t(`proposal.status.${v.status}`)}
                        />
                        {v.active && (
                          <Chip
                            size="small"
                            color="primary"
                            label={t('proposal.versions.active')}
                          />
                        )}
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ flex: 1, minWidth: 120 }}
                        >
                          {v.submittedAt
                            ? t('proposal.versions.submittedOn', {
                                date: formatDate(v.submittedAt),
                              })
                            : t('proposal.versions.updatedOn', {
                                date: formatDate(v.updatedAt),
                              })}
                        </Typography>
                        {isCurrentView ? (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={t('proposal.versions.viewing')}
                          />
                        ) : v.active ? (
                          <Button size="small" onClick={backToActive} disabled={busy}>
                            {t('proposal.versions.goActive')}
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            onClick={() => void viewVersion(v.id)}
                            disabled={busy}
                          >
                            {t('proposal.versions.view')}
                          </Button>
                        )}
                      </Stack>
                    );
                  })}
                </Stack>
              </Paper>
            )}
          </>
        )}

        <Snackbar
          open={msg !== null}
          autoHideDuration={3000}
          onClose={() => setMsg(null)}
          message={msg ?? ''}
        />

        {/* ─── Confirmar la presentación a la red ───────────────────────────
          DOS ACTOS DISTINTOS, y por eso no alcanza un "¿seguro?". Una CONSULTA
          pide comentarios y no compromete nada. Una SOLICITUD pide la
          certificación del programa: si TMAI la aprueba, la certificación queda
          otorgada. Elegir entre las dos es del centro, no de la plataforma. */}
        <Dialog
          open={sendKind !== null}
          onClose={busy ? undefined : () => setSendKind(null)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle sx={{ fontWeight: 700 }}>{t('proposal.network.confirmTitle')}</DialogTitle>
          {busy && <LinearProgress />}
          <DialogContent>
            <Stack spacing={2}>
              {/* El error del envío se mostraba SOLO en el Alert de la página, que
                  este diálogo tapa — y el diálogo sigue abierto justamente cuando
                  falla. Desde el otro lado eso es un botón que no hace nada. */}
              {error && (
                <Alert severity="error" onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}
              <Typography variant="body2">{t('proposal.network.confirmBody')}</Typography>
              <RadioGroup
                value={sendKind ?? 'formal'}
                onChange={(e) => setSendKind(e.target.value as 'advisory' | 'formal')}
              >
                <FormControlLabel
                  value="formal"
                  control={<Radio />}
                  disabled={busy}
                  label={
                    <Box>
                      <Typography fontWeight={700}>{t('proposal.network.formalTitle')}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('proposal.network.formalHelp')}
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="advisory"
                  control={<Radio />}
                  disabled={busy}
                  label={
                    <Box>
                      <Typography fontWeight={700}>
                        {t('proposal.network.advisoryTitle')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('proposal.network.advisoryHelp')}
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
              {/* LA EVIDENCIA, ANTES DE FIRMAR. La atestación de brechas se
                  marcaba sin tener las brechas delante. */}
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight={700}>
                  {t('proposal.network.auditTitle')}
                </Typography>
                {audit ? (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      {t('proposal.network.auditTcu', {
                        actual: audit.totalActualTcu,
                        min: audit.totalMinTcu,
                        status: t(`compliance.status.${audit.overallStatus}`),
                      })}
                    </Typography>
                    {audit.gaps.length > 0 && (
                      <Typography variant="body2" color="warning.main">
                        {t('proposal.network.auditAreaGaps', { areas: audit.gaps.join(', ') })}
                      </Typography>
                    )}
                    {audit.plosPending > 0 && (
                      <Typography variant="body2" color="warning.main">
                        {t('proposal.network.auditPlos', {
                          n: audit.plosPending,
                          total: audit.plosTotal,
                        })}
                      </Typography>
                    )}
                    {audit.nonAcademicPending > 0 && (
                      <Typography variant="body2" color="warning.main">
                        {t('proposal.network.auditNonAcademic', { n: audit.nonAcademicPending })}
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography variant="body2" color="error">
                    {t('proposal.network.auditMissing')}
                  </Typography>
                )}
              </Paper>

              {/* Lo único que el centro FIRMA. */}
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={cvsAttested}
                      onChange={(e) => setCvsAttested(e.target.checked)}
                      disabled={busy}
                    />
                  }
                  label={t('proposal.attestFacultyCvs')}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={gapsAttested}
                      onChange={(e) => setGapsAttested(e.target.checked)}
                      disabled={busy}
                    />
                  }
                  label={t('proposal.attestKnownGaps')}
                />
              </Box>

              {/* Lo que viaja, dicho antes de que viaje. */}
              <Alert severity="info" variant="outlined">
                {t('proposal.network.noPii')}
              </Alert>
              <Alert severity="warning" variant="outlined">
                {t('proposal.network.recorded')}
              </Alert>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => setSendKind(null)}
              disabled={busy}
              sx={{ textTransform: 'none' }}
            >
              {t('proposal.network.cancel')}
            </Button>
            <Button
              variant="contained"
              disabled={busy}
              onClick={() => void sendToNetwork(sendKind ?? 'formal')}
              sx={{ textTransform: 'none' }}
            >
              {t('proposal.network.confirmCta')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}

/** Resumen de la última auditoría del programa+CAP+nivel, tal como la verá el
 *  revisor. Se lee de `capComplianceReports` (staff-readable) y es best-effort:
 *  sin auditoría, el diálogo lo dice en vez de mostrar ceros. */
async function loadAuditSummary(
  tenantId: string,
  programId: string,
  capCode: CapCode,
  level: AcademicLevel,
): Promise<{
  overallStatus: 'compliant' | 'with_gaps';
  totalMinTcu: number;
  totalActualTcu: number;
  gaps: string[];
  plosPending: number;
  plosTotal: number;
  nonAcademicPending: number;
} | null> {
  try {
    const snap = await getDocs(
      query(
        collection(db, `tenants/${tenantId}/capComplianceReports`),
        where('programId', '==', programId),
        where('capCode', '==', capCode),
        orderBy('generatedAt', 'desc'),
        limit(1),
      ),
    );
    const report = snap.docs[0]?.data();
    if (!report || report['level'] !== level) return null;
    const areas = (report['subjectAreas'] ?? []) as Array<{ code: string; gapTcu: number }>;
    const plos = (report['ploCoverage'] ?? []) as Array<{ evaluated: boolean }>;
    const nonAcademic = (report['nonAcademic'] ?? []) as Array<{ met: boolean }>;
    return {
      overallStatus: report['overallStatus'] as 'compliant' | 'with_gaps',
      totalMinTcu: report['totalMinTcu'] as number,
      totalActualTcu: report['totalActualTcu'] as number,
      gaps: areas.filter((a) => a.gapTcu > 0).map((a) => `${a.code} (${a.gapTcu} TCU)`),
      plosPending: plos.filter((p) => !p.evaluated).length,
      plosTotal: plos.length,
      nonAcademicPending: nonAcademic.filter((n) => !n.met).length,
    };
  } catch {
    return null;
  }
}
